import { useContext, useEffect, useRef, useState } from "react";
import { Camera, FileUp, LoaderCircle, X } from "lucide-react";
import { AuthContext } from "@/auth/AuthContext";
import { pick } from "@/lib/scegliFile";
import { comprimiFoto } from "@/lib/comprimiFoto";
import { cancellaPagine, leggiPagine, salvaPagine } from "@/lib/pagineScattate";
import { PannelloBasso } from "@/components/ui/PannelloBasso";
import {
  ALTRA_PAGINA, FOTOGRAFA_ALTRA, ANNULLA, ANNULLA_BOLLETTA, BOLLETTA_FINITA, BOLLETTA_NUOVA, CARICA_BREVE, CARICA_DAL_TELEFONO, FOTOGRAFA,
  TORNA_ALLE_FOTO, pagine as quantePagine, paginePerse, togliPagina,
} from "./scritte";

const ACCETTA_FOTO = "image/jpeg,image/png";
/** Come il server (`MAX_PAGINE`): le bollette dei condomìni hanno 3-12 pagine. */
const MAX_PAGINE = 15;

/** Una pagina scattata, con l'indirizzo temporaneo della sua miniatura (rilasciato quando la pagina se ne va). */
interface Pagina {
  file: Blob;
  url: string;
}

function conAnteprima(file: Blob): Pagina {
  return { file, url: typeof URL.createObjectURL === "function" ? URL.createObjectURL(file) : "" };
}

function rilascia(p: Pagina) {
  if (p.url) URL.revokeObjectURL(p.url);
}

const PULSANTE = "inline-flex items-center justify-center gap-2 rounded-lg cursor-pointer transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed";
const PRINCIPALE = `${PULSANTE} h-12 bg-brand-dark px-4 font-semibold text-white hover:bg-dark`;
// sul Fold chiuso (344 px) i due pulsanti del pannello stanno in una riga solo col carattere più piccolo e 12 px di margine
const STRETTO = "max-[369px]:px-3! max-[369px]:text-sm";
const SECONDARIO = `${PULSANTE} border border-border bg-card font-medium text-text hover:bg-surface`;

interface Props {
  /** «Carica PDF o foto dal telefono»: il caricamento di sempre, ogni file una bolletta. */
  onCarica: () => void;
  /** «Bolletta finita»: le pagine vanno in coda d'invio (sul telefono) e partono da sole; la promessa si risolve a
   *  coda salvata. */
  onFinita: (pagine: Blob[]) => Promise<void>;
  /** «Rifai la foto»: l'id della bolletta da rifare. Quando arriva, il pannello si apre vuoto. */
  sostituisce?: string | null;
  /** Il pannello della sostituzione si è chiuso («Bolletta finita» o «Annulla»). */
  onSostituzioneChiusa?: () => void;
  /** Nel preventivo c'è già una bolletta (anche in coda d'invio): il pulsante dice «Fotografa un'altra bolletta». */
  haBollette?: boolean;
  /** La frase del tetto di bollette: con lei i due comandi per una bolletta nuova sono spenti e la frase sta sotto. */
  tetto?: string;
}

/** Sul telefono e sul Fold (sotto i 1024 px): «Fotografa una bolletta» e «Carica PDF o foto dal telefono». Le pagine
 *  scattate aspettano nel pannello dal basso, ferme e salvate sul telefono; «Bolletta finita» chiude subito il pannello
 *  e le passa alla coda d'invio come UNA bolletta: si fotografa la successiva mentre la prima parte (mockup
 *  foto-bollette, variante A). */
export function FotoBolletta({ onCarica, onFinita, sostituisce, onSostituzioneChiusa, haBollette = false, tetto }: Props) {
  // per utente: sullo stesso telefono le pagine di uno non riappaiono a un altro
  const chiave = `pagine:${useContext(AuthContext)?.user?.id ?? ""}`;
  const [pagine, setPagine] = useState<Pagina[]>([]);
  const pagineRef = useRef<Pagina[]>([]);
  const [aperto, setAperto] = useState(false);
  const [conferma, setConferma] = useState(false);
  // foto scattate e ancora in compressione; `epoca` cambia a «Annulla»: le preparazioni in corso si scartano
  const [preparando, setPreparando] = useState(0);
  const epoca = useRef(0);
  const striscia = useRef<HTMLUListElement>(null);

  const aggiorna = (nuove: Pagina[]) => {
    pagineRef.current = nuove;
    setPagine(nuove);
    void salvaPagine(chiave, nuove.map((p) => p.file));
  };
  useEffect(() => () => pagineRef.current.forEach(rilascia), []);

  // Al ritorno nella pagina (anche dopo che il browser ha scartato la scheda) il pannello riappare con le pagine già
  // scattate. Se nel frattempo se n'è scattata una, vince quella in memoria.
  useEffect(() => {
    let vivo = true;
    void leggiPagine(chiave).then((salvate) => {
      if (!vivo || salvate.length === 0 || pagineRef.current.length > 0) return;
      pagineRef.current = salvate.map(conAnteprima);
      setPagine(pagineRef.current);
      setAperto(true);
    });
    return () => { vivo = false; };
  }, [chiave]);

  // «Rifai la foto» riapre il pannello, vuoto: le pagine vecchie non c'entrano
  useEffect(() => {
    if (!sostituisce) return;
    setAperto(true);
    setConferma(false);
  }, [sostituisce]);

  const scatta = () => pick(ACCETTA_FOTO, "environment", (files) => {
    const mia = epoca.current;
    setPreparando((n) => n + files.length);
    setAperto(true);
    setConferma(false);
    void Promise.all(files.map(comprimiFoto)).then((leggere) => {
      if (mia !== epoca.current) return;
      setPreparando((n) => n - files.length);
      const tutte = [...pagineRef.current, ...leggere.map(conAnteprima)];
      tutte.slice(MAX_PAGINE).forEach(rilascia);
      aggiorna(tutte.slice(0, MAX_PAGINE));
    });
  });

  // l'ultima pagina scattata si vede sempre, anche quando le miniature non stanno più nella larghezza
  const quante = pagine.length + preparando;
  const prima = useRef(quante);
  useEffect(() => {
    if (quante > prima.current) striscia.current?.scrollTo?.({ left: striscia.current.scrollWidth });
    prima.current = quante;
  }, [quante]);

  const togli = (i: number) => {
    rilascia(pagineRef.current[i]);
    aggiorna(pagineRef.current.filter((_, j) => j !== i));
  };

  const chiudi = () => {
    epoca.current += 1;
    setPreparando(0);
    pagineRef.current.forEach(rilascia);
    pagineRef.current = [];
    setPagine([]);
    void cancellaPagine(chiave);
    setAperto(false);
    setConferma(false);
    onSostituzioneChiusa?.();
  };

  // Il pannello si chiude appena la bolletta è in coda (salvata sul telefono): non aspetta il server. Un secondo tocco
  // non trova più pagine e non manda niente.
  async function finita() {
    const file = pagineRef.current.map((p) => p.file);
    if (file.length === 0) return;
    pagineRef.current.forEach(rilascia);
    pagineRef.current = [];
    setPagine([]);
    setAperto(false);
    onSostituzioneChiusa?.();
    await onFinita(file);
    void cancellaPagine(chiave);
  }

  const annulla = () => {
    if (pagineRef.current.length === 0) chiudi();
    else setConferma(true);
  };

  return (
    <div className="flex flex-col gap-2 sm:grid sm:grid-cols-[3fr_2fr] lg:hidden">
      <button type="button" onClick={scatta} disabled={tetto !== undefined} className={`${PRINCIPALE} w-full`}>
        <Camera size={20} aria-hidden="true" /> {haBollette ? FOTOGRAFA_ALTRA : FOTOGRAFA}
      </button>
      <button type="button" onClick={onCarica} disabled={tetto !== undefined} aria-label={CARICA_DAL_TELEFONO} className={`${SECONDARIO} h-11 w-full px-4 text-sm sm:h-12`}>
        <FileUp size={16} aria-hidden="true" />
        {/* da 640 px la scritta intera non sta nel 2fr: lì è «Carica PDF o foto» (approvata il 25/10) */}
        <span className="sm:hidden">{CARICA_DAL_TELEFONO}</span>
        <span className="hidden sm:inline">{CARICA_BREVE}</span>
      </button>

      <p aria-live="polite" className="text-xs text-muted-foreground empty:hidden sm:col-span-2">{tetto}</p>

      <PannelloBasso aperto={aperto} onChiudi={annulla} sopra={BOLLETTA_NUOVA} titolo={quantePagine(pagine.length)} testoChiudi={ANNULLA}>
        {/* Altezza fissa: le pagine scorrono di lato e i pulsanti sotto non si spostano mai. */}
        <ul ref={striscia} className="-mx-1 flex h-[112px] gap-3 overflow-x-auto overscroll-contain pl-1 pr-4 pt-4" aria-label={quantePagine(pagine.length)}>
          {pagine.length === 0 && preparando === 0 && (
            <li aria-hidden="true" className="h-[92px] w-[68px] shrink-0 rounded-md border-2 border-dashed border-border" />
          )}
          {pagine.map((p, i) => (
            <Miniatura key={p.url || i} url={p.url} numero={i + 1} onTogli={() => togli(i)} />
          ))}
          {Array.from({ length: preparando }, (_, k) => (
            <li
              key={`prep${k}`}
              data-testid="pagina-in-preparazione"
              className="relative flex h-[92px] w-[68px] shrink-0 items-center justify-center rounded-md border-2 border-dashed border-border"
            >
              <LoaderCircle size={20} className="animate-spin motion-reduce:animate-none text-muted-foreground" aria-hidden="true" />
              <span className="absolute bottom-1 left-1 rounded px-1 text-[11px] font-semibold text-text tabular-nums">{pagine.length + k + 1}</span>
            </li>
          ))}
        </ul>

        {conferma ? (
          <div className="mt-4 space-y-3">
            <p className="text-sm text-text">{paginePerse(pagine.length)}</p>
            {/* Uno sopra l'altro: sul Fold chiuso, affiancati, «Annulla la bolletta» va a capo. In basso, dove c'erano
                «Altra pagina» e «Bolletta finita», il pulsante che non perde niente. */}
            <div className="grid gap-2">
              <button type="button" onClick={chiudi} className={`${PULSANTE} h-12 bg-red-700 px-3 font-semibold text-white hover:bg-red-800`}>
                {ANNULLA_BOLLETTA}
              </button>
              <button type="button" onClick={() => setConferma(false)} className={`${SECONDARIO} h-12 px-3`}>
                {TORNA_ALLE_FOTO}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <button type="button" onClick={scatta} disabled={pagine.length >= MAX_PAGINE} className={`${SECONDARIO} h-12 whitespace-nowrap px-3 ${STRETTO}`}>
                {/* sul Fold chiuso (344 px) «Aggiungi pagina» con l'icona va a capo: lì resta il solo testo */}
                <Camera size={20} className="hidden min-[370px]:block" aria-hidden="true" /> {ALTRA_PAGINA}
              </button>
              {/* spento mentre una foto si prepara: lo spinner sta già sulla sua miniatura (a 344 px nel pulsante andava a capo) */}
              <button type="button" onClick={() => { void finita(); }} disabled={pagine.length === 0 || preparando > 0} aria-busy={preparando > 0} className={`${PRINCIPALE} whitespace-nowrap px-3 ${STRETTO}`}>
                {BOLLETTA_FINITA}
              </button>
            </div>
          </>
        )}
      </PannelloBasso>
    </div>
  );
}

/** Una pagina scattata: la miniatura col suo numero, e la × per toglierla (bersaglio di 44×44, il cerchio ne mostra 28). */
function Miniatura({ url, numero, onTogli }: { url: string; numero: number; onTogli: () => void }) {
  return (
    <li className="relative h-[92px] w-[68px] shrink-0">
      {url && <img src={url} alt="" className="h-full w-full rounded-md border border-border object-cover" />}
      <span className="absolute bottom-1 left-1 rounded bg-white/90 px-1 text-[11px] font-semibold text-text tabular-nums">{numero}</span>
      <button
        type="button"
        onClick={onTogli}
        aria-label={togliPagina(numero)}
        className="group absolute -right-3.5 -top-3.5 inline-flex size-11 items-center justify-center rounded-full cursor-pointer focus-visible:outline-none"
      >
        <span className="inline-flex size-7 items-center justify-center rounded-full bg-dark text-white transition-colors group-hover:bg-black group-focus-visible:ring-2 group-focus-visible:ring-brand">
          <X size={16} aria-hidden="true" />
        </span>
      </button>
    </li>
  );
}
